import lodashGet from "lodash/get";
import lodashSet from "lodash/set";

const check = (it: unknown) => {
  // Math is known to exist as a global in every environment.
  return it && (it as { Math: unknown }).Math === Math && it;
};

/**
 * The global object. This is the object that all global variables are attached to.
 * This should not be accessed directly, use `defineGlobal` instead.
 */
export const globalObject =
  // IE11 doesn't support globalThis.
  check(typeof globalThis === "object" && globalThis) ||
  check(typeof window === "object" && window) ||
  check(typeof self === "object" && self) ||
  check(typeof global === "object" && global) ||
  Function("return this")();

type DefineGlobalOptions = {
  /**
   * When true, the global variable will not be stored on the `faire` namespace.
   * This should only be used for 3rd party integrations that expect a certain global variable to be present.
   */
  escapeFaireNamespace?: boolean;
};

export type Global<T> = {
  get: {
    (defaultValue: T): T;
    (defaultValue?: T): T | undefined;
  };
  set: (value: T) => T;
  setIfAbsent: (value: T) => T;
};

/**
 * Defines a global variable that is stored on the global object. This should NEVER be used with user specific data, as it is not request scoped on the server.
 * This should ONLY be used when declaring a normal top-level variable does not work, for example when multiple copies of a module
 * are created by Next.js.
 */
export function defineGlobal<T>(
  name: string,
  options?: DefineGlobalOptions
): Global<T> {
  const prop = options?.escapeFaireNamespace ? name : `faire.${name}`;

  function get(defaultValue: T): T;
  function get(defaultValue?: T): T | undefined;
  function get(defaultValue?: T): T | undefined {
    return lodashGet(globalObject, prop) ?? defaultValue;
  }

  function set(value: T): T {
    lodashSet(globalObject, prop, value);
    return value;
  }

  function setIfAbsent(value: T): T {
    const currentValue = get();
    if (currentValue === undefined) {
      set(value);
      return value;
    } else {
      return currentValue;
    }
  }

  return { get, set, setIfAbsent };
}
